import type { FC, MouseEvent } from 'react'
import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import {
  ChevronLeftDouble,
  ChevronRightDouble,
  Plus,
  Stars01,
} from '@untitled-ui/icons-react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import Drawer from '@mui/material/Drawer'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'
// import { Logo } from '@/components/Logo'
// import { RouterLink } from '@/components/RouterLink'
import { usePathname } from '@/hooks/use-pathname'
// import { paths } from '@/paths'
import type { NavColor } from '@/types/settings'
import type { Section } from '../config'
import { SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX } from './side-nav-item'
import {
  SIDE_NAV_DRAWER_EXPANDED_PX,
  SIDE_NAV_DRAWER_FOLDED_PX,
  SIDE_NAV_ITEM_FONT_SIZE,
  sideNavButtonHoverSx,
} from './side-nav-layout-dimensions'
import { densityPx, densityPxStr } from '@/theme/base/density'
import { SideNavSection } from './side-nav-section'
import DialogUpload from '@/components/DialogUpload'
import { useRightCheck } from '@/components/RightGate'
import useTranslate from '@/hooks/use-translate'
import { useAuth } from '@/hooks/use-auth'
import * as Sentry from '@sentry/browser'
import Divider from '@mui/material/Divider'
import { SideNavAccountFooter } from './side-nav-account-footer'
import Typography from '@mui/material/Typography'
import { VersionDisplayer } from '../version-displayer'
import {
  WhatsNewModal,
  type ChangelogReleasesState,
} from '@/components/WhatsNewModal'
import { useWhatsNewAutoOpen } from '@/components/WhatsNewModal/hooks/use-whats-new-auto-open'
import { SideNavHelpEntry } from './side-nav-help-entry'
import { setIntercomSideNavWidth } from '@/hooks/use-intercom-messenger'
import { SideNavProductTourEntry } from './side-nav-product-tour-entry'
import { CommandBarTrigger } from '@/components/CommandBar'
import { NotificationBell } from '@/sections/notifications'

const SIDE_NAV_LINK_SPACING = 1
/** Écart entre deux entrées, le même que dans `SideNavSection`. */
const SIDE_NAV_ITEM_GAP = 0.5
const SIDE_NAV_NEW_FOLDER_MARGIN = 2

const SIDE_NAV_LOGO_HEIGHT_PX = densityPx(50)
/** `mt` de la ligne du logo ; son `py` (2) s'y ajoute, lu dans le thème. */
const SIDE_NAV_LOGO_ROW_MARGIN_TOP_PX = 14
/** Même taille pour le bouton de repli et la poignée de dépliage. */
const SIDE_NAV_FOLD_BUTTON_PX = 20

/** Largeur logo plein (px). Le compact est dérivé du ratio historique design 40 / 163. */
const SIDE_NAV_LOGO_FULL_WIDTH_PX = 130
const SIDE_NAV_LOGO_COMPACT_WIDTH_PX = Math.round(
  (densityPx(40) * SIDE_NAV_LOGO_FULL_WIDTH_PX) / densityPx(163)
)
/** Hauteur rendue des logos, collés en haut de leur boîte : `logo.svg` est en 110 × 27, `logo-small.svg` carré. */
const SIDE_NAV_LOGO_FULL_HEIGHT_PX = Math.round(
  (SIDE_NAV_LOGO_FULL_WIDTH_PX * 27) / 110
)
const SIDE_NAV_LOGO_COMPACT_HEIGHT_PX = SIDE_NAV_LOGO_COMPACT_WIDTH_PX
const HANDLE_OFFSET_IN_LOGO_PX =
  (SIDE_NAV_LOGO_COMPACT_HEIGHT_PX - SIDE_NAV_FOLD_BUTTON_PX) / 2

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme()

  return useMemo((): Record<string, string> => {
    switch (color) {
      case 'blend-in':
        return {
          '--nav-bg': theme.palette.neutral[900],
          '--nav-color': theme.palette.neutral[100],
          '--nav-border-color': theme.palette.neutral[700],
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.08)',
          '--nav-item-active-bg': 'rgba(19, 98, 132, 0.5)',
          '--nav-item-active-hover-bg': 'rgba(19, 98, 132, 0.7)',
          '--nav-item-active-color': theme.palette.neutral[100],
          '--nav-item-disabled-color': theme.palette.neutral[600],
          '--nav-item-icon-color': theme.palette.neutral[500],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[700],
          '--nav-item-chevron-color': theme.palette.neutral[700],
          '--nav-scrollbar-color': theme.palette.neutral[400],
        }

      case 'discreet':
        return {
          '--nav-bg': theme.palette.neutral[900],
          '--nav-color': theme.palette.neutral[100],
          '--nav-border-color': theme.palette.neutral[700],
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.08)',
          '--nav-item-active-bg': 'rgba(19, 98, 132, 0.5)',
          '--nav-item-active-hover-bg': 'rgba(19, 98, 132, 0.7)',
          '--nav-item-active-color': theme.palette.neutral[100],
          '--nav-item-disabled-color': theme.palette.neutral[600],
          '--nav-item-icon-color': theme.palette.neutral[500],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[700],
          '--nav-item-chevron-color': theme.palette.neutral[700],
          '--nav-scrollbar-color': theme.palette.neutral[400],
        }

      case 'evident':
        return {
          '--nav-bg': theme.palette.neutral[800],
          '--nav-color': theme.palette.common.white,
          '--nav-border-color': 'transparent',
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.12)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.08)',
          '--nav-item-active-hover-bg': 'rgba(255, 255, 255, 0.14)',
          '--nav-item-active-color': theme.palette.common.white,
          '--nav-item-disabled-color': theme.palette.neutral[500],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[500],
          '--nav-item-chevron-color': theme.palette.neutral[600],
          '--nav-scrollbar-color': theme.palette.neutral[400],
        }

      default:
        return {}
    }
  }, [theme, color])
}

/** Clics sur couches MUI portées remontent quand même en capture sur le Paper du Drawer (arbre React). */
const MUI_PORTAL_CLICK_IGNORE_SELECTOR =
  '.MuiModal-root, .MuiPopover-root, .MuiMenu-root, [role="dialog"], [role="menuitem"], [role="option"]'

interface SideNavProps {
  color?: NavColor
  sections?: Section[]
  changelog: ChangelogReleasesState
  isFolded?: boolean
  onToggleSideNav?: () => void
  transitionDurationMs?: number
  /** Libellés affichés dès le début du dépliage (changement de page), pas à la fin de l'animation. */
  revealLabelsImmediately?: boolean
  /** Dans le cadre d'application (essai TEC-1956), sans bord : le contenu s'en détache seul. */
  framed?: boolean
}

export const SideNav: FC<SideNavProps> = ({
  color = 'evident',
  sections = [],
  changelog,
  isFolded = false,
  onToggleSideNav,
  transitionDurationMs = 240,
  revealLabelsImmediately = false,
  framed = false,
}) => {
  const { t: tNav } = useTranslate('nav')
  const { t: tDash } = useTranslate('dashboard')
  const createRight = useRightCheck('declarations.create')
  const { t: tWhatsNew } = useTranslate('whatsNew')
  const [openDialog, setOpenModal] = useState(false)
  const [showText, setShowText] = useState(!isFolded)
  const drawerWidth = isFolded
    ? SIDE_NAV_DRAWER_FOLDED_PX
    : SIDE_NAV_DRAWER_EXPANDED_PX
  // La messagerie support, ouverte à gauche, se place à côté de la sidenav.
  useEffect(() => {
    setIntercomSideNavWidth(drawerWidth)
    return () => setIntercomSideNavWidth(0)
  }, [drawerWidth])
  const {
    releases: changelogReleases,
    isLoading: changelogLoading,
    isEmpty: changelogEmpty,
    hasReleases: changelogHasReleases,
    showNewBadge,
    latestReleaseId,
    acknowledgeOnUnderstood: acknowledgeChangelogRelease,
  } = changelog
  const { open: whatsNewOpen, setOpen: setWhatsNewOpen } = useWhatsNewAutoOpen({
    isLoading: changelogLoading,
    showNewBadge,
    latestReleaseId,
  })

  const auth = useAuth()

  const pathname = usePathname()
  const cssVars = useCssVars(color)
  /** Synchrone : évite de replier la sidenav au clic « dehors » qui ferme le menu compte (phase capture). */
  const accountMenuOpenRef = useRef(false)

  const handleClickOpen = () => {
    setOpenModal(true)
  }

  const handleClose = () => {
    setOpenModal(false)
  }

  // IMPORTANT !!! set localstorage, needs to always render
  useEffect(() => {
    localStorage.setItem('user', auth?.user ? JSON.stringify(auth?.user) : '')
    if (auth?.user?.email) {
      Sentry.setUser({
        email: auth?.user?.email,
      })
    }
  }, [auth.user])

  const handleDrawerToggleZoneClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!onToggleSideNav) return
      if (accountMenuOpenRef.current) return
      const el = e.target as HTMLElement | null
      if (!el) return
      if (
        el.closest('a[href], button, input, textarea, select, [role="button"]')
      ) {
        return
      }
      if (el.closest(MUI_PORTAL_CLICK_IGNORE_SELECTOR)) {
        return
      }
      onToggleSideNav()
    },
    [onToggleSideNav]
  )

  // Avant peinture : sur un changement de page, les libellés sont là dès la première image du dépliage.
  useLayoutEffect(() => {
    if (!isFolded) {
      if (revealLabelsImmediately) {
        setShowText(true)
        return
      }
      const timeout = setTimeout(() => {
        setShowText(true)
      }, transitionDurationMs)

      return () => clearTimeout(timeout)
    } else {
      setShowText(false)
    }
  }, [isFolded, transitionDurationMs, revealLabelsImmediately])

  return (
    <Drawer
      anchor="left"
      open
      PaperProps={{
        ...(onToggleSideNav
          ? { onClickCapture: handleDrawerToggleZoneClick }
          : {}),
        sx: {
          ...cssVars,
          backgroundColor: 'var(--nav-bg)',
          borderRightColor: framed ? 'transparent' : 'var(--nav-border-color)',
          borderRightStyle: 'solid',
          borderRightWidth: 1,
          color: 'var(--nav-color)',
          width: drawerWidth,
          overflowX: 'hidden',
          ...(onToggleSideNav ? { cursor: 'pointer' as const } : {}),
          // Replier et déplier n'apparaissent qu'au survol de la sidenav (ou au focus clavier).
          '&:hover .side-nav-fold-toggle, & .side-nav-fold-toggle:focus-visible':
            { opacity: 1 },
          transition: (theme) =>
            theme.transitions.create('width', {
              duration: transitionDurationMs,
              easing: theme.transitions.easing.sharp,
            }),
        },
      }}
      variant="permanent"
    >
      <Stack
        spacing={SIDE_NAV_NEW_FOLDER_MARGIN}
        sx={{ height: '100%', minHeight: 0 }}
      >
        <Stack
          spacing={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexGrow: 1,
            minHeight: 0,
            overflow: 'hidden',
            px: 2,
          }}
        >
          <Stack
            alignItems="flex-start"
            direction="row"
            justifyContent="space-between"
            // `gap` et non `spacing` : `spacing` remet à zéro la marge qui centre le bouton de repli sur le logo.
            sx={{
              alignSelf: 'stretch',
              flexShrink: 0,
              gap: 1,
              py: 2,
              mt: `${SIDE_NAV_LOGO_ROW_MARGIN_TOP_PX}px`,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                height: SIDE_NAV_LOGO_HEIGHT_PX,
                width: isFolded
                  ? SIDE_NAV_LOGO_COMPACT_WIDTH_PX
                  : SIDE_NAV_LOGO_FULL_WIDTH_PX,
              }}
            >
              <img
                src="/logo.svg"
                alt="logo full"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: isFolded ? 0 : 1,
                  width: isFolded ? 0 : SIDE_NAV_LOGO_FULL_WIDTH_PX,
                }}
              />
              <img
                src="/logo-small.svg"
                alt="logo small"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  opacity: isFolded ? 1 : 0,
                  width: isFolded ? SIDE_NAV_LOGO_COMPACT_WIDTH_PX : 0,
                }}
              />
            </Box>
            {onToggleSideNav && !isFolded && showText && (
              <Tooltip
                enterDelay={300}
                placement="right"
                title={tNav('collapseSideNav')}
              >
                <IconButton
                  aria-label={tNav('collapseSideNav')}
                  className="side-nav-fold-toggle"
                  data-testid="side-nav-collapse-button"
                  size="small"
                  onClick={onToggleSideNav}
                  sx={{
                    border: '1px solid var(--nav-border-color)',
                    borderRadius: '5px',
                    color: 'var(--nav-item-icon-color)',
                    height: SIDE_NAV_FOLD_BUTTON_PX,
                    // Centré sur le logo, pas sur sa boîte.
                    mt: `${
                      (SIDE_NAV_LOGO_FULL_HEIGHT_PX - SIDE_NAV_FOLD_BUTTON_PX) /
                      2
                    }px`,
                    opacity: 0,
                    p: 0,
                    transition: 'opacity 0.15s ease',
                    width: SIDE_NAV_FOLD_BUTTON_PX,
                    '&:hover': sideNavButtonHoverSx,
                  }}
                >
                  <SvgIcon sx={{ fontSize: 12 }}>
                    <ChevronLeftDouble />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {/* Replié : la poignée chevauche le bord. En `fixed`, le `overflowX: hidden` du Paper ne la coupe pas. */}
          {onToggleSideNav && isFolded && (
            <Tooltip
              enterDelay={300}
              placement="right"
              title={tNav('expandSideNav')}
            >
              <IconButton
                aria-label={tNav('expandSideNav')}
                className="side-nav-fold-toggle"
                data-testid="side-nav-expand-handle"
                onClick={onToggleSideNav}
                sx={{
                  backgroundColor: 'var(--nav-bg)',
                  border: '1px solid var(--nav-border-color)',
                  borderRadius: '5px',
                  color: 'var(--nav-item-icon-color)',
                  height: SIDE_NAV_FOLD_BUTTON_PX,
                  left: SIDE_NAV_DRAWER_FOLDED_PX - SIDE_NAV_FOLD_BUTTON_PX / 2,
                  opacity: 0,
                  p: 0,
                  position: 'fixed',
                  top: (theme) =>
                    `calc(${SIDE_NAV_LOGO_ROW_MARGIN_TOP_PX}px + ${theme.spacing(
                      2
                    )} + ${HANDLE_OFFSET_IN_LOGO_PX}px)`,
                  transition: 'opacity 0.15s ease',
                  width: SIDE_NAV_FOLD_BUTTON_PX,
                  zIndex: 1,
                  '&:hover': {
                    backgroundColor: 'var(--nav-bg)',
                    color: 'var(--nav-item-active-color)',
                  },
                }}
              >
                <SvgIcon sx={{ fontSize: 12 }}>
                  <ChevronRightDouble />
                </SvgIcon>
              </IconButton>
            </Tooltip>
          )}
          <Box
            sx={{
              alignSelf: 'stretch',
              flexShrink: 0,
              my: SIDE_NAV_NEW_FOLDER_MARGIN,
              width: '100%',
            }}
          >
            <Stack
              alignItems="flex-start"
              direction="row"
              sx={{
                justifyContent: 'flex-start',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flex: '1 1 auto',
                  justifyContent: 'flex-start',
                  minWidth: 0,
                  width: '100%',
                }}
              >
                {/* Créer un dossier suit les droits du membre (TEC-1677) : grisé, l'infobulle dit pourquoi. */}
                {createRight.state !== 'hidden' && (
                <Tooltip
                  disableHoverListener={!isFolded && !createRight.reason}
                  enterDelay={300}
                  placement="right"
                  title={createRight.reason ?? tDash('newFolderButton')}
                >
                  <Box sx={{ display: 'block', width: '100%' }}>
                    <Button
                      data-testid="button-new-folder-in-sidebar"
                      disabled={createRight.state !== 'enabled'}
                      fullWidth
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: showText ? 1 : 0,
                        height: densityPx(40),
                        justifyContent: 'center',
                        minWidth: 'unset',
                        overflow: 'hidden',
                        pl: `${SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX}px`,
                        pr: `${densityPx(10)}px`,
                        py: 0.5,
                        textAlign: 'center',
                        width: isFolded ? 'fit-content' : '100%',
                      }}
                      variant="contained"
                      onClick={handleClickOpen}
                    >
                      <Box
                        component="span"
                        sx={{
                          alignItems: 'center',
                          color: 'inherit',
                          display: 'inline-flex',
                          flex: '0 0 auto',
                          height: densityPx(28),
                          justifyContent: 'center',
                        }}
                      >
                        <SvgIcon fontSize="small">
                          <Plus />
                        </SvgIcon>
                      </Box>
                      {!isFolded && (
                        <Typography
                          sx={{
                            color: 'inherit',
                            flex: showText ? '0 1 auto' : '0 0 0px',
                            fontSize: SIDE_NAV_ITEM_FONT_SIZE,
                            fontWeight: 600,
                            lineHeight: `${densityPx(28)}px`,
                            maxWidth: showText ? 'none' : 0,
                            minWidth: 0,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            opacity: showText ? 1 : 0,
                            pointerEvents: showText ? 'auto' : 'none',
                            transform: showText
                              ? 'translateX(0)'
                              : `translateX(-${densityPx(10)}px)`,
                            transition:
                              'opacity 0.3s ease, transform 0.3s ease, max-width 0.3s ease',
                          }}
                        >
                          {tDash('newFolderButton')}
                        </Typography>
                      )}
                    </Button>
                  </Box>
                </Tooltip>
                )}
              </Box>
            </Stack>
            <DialogUpload
              open={openDialog}
              onClose={handleClose}
              data-testid="dialog-upload-new-folder"
            />
          </Box>
          <Box sx={{ alignSelf: 'stretch', flexShrink: 0 }}>
            <CommandBarTrigger isFolded={isFolded} showText={showText} />
          </Box>
          <Stack
            component="nav"
            spacing={SIDE_NAV_LINK_SPACING}
            sx={{
              alignSelf: 'stretch',
              flexGrow: 1,
              minHeight: 0,
              overflow: 'auto',
              width: '100%',
              // Plus bas que la recherche : deux blocs distincts.
              marginTop: 5,
            }}
          >
            {sections.map((section, index) => (
              <SideNavSection
                items={section.items}
                key={index}
                labelsVisible={showText}
                pathname={pathname}
                subheader={section.subheader}
                isFolded={isFolded}
              />
            ))}
          </Stack>
        </Stack>
        <Box
          gap={SIDE_NAV_ITEM_GAP}
          sx={{
            display: 'flex',
            flexShrink: 0,
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2,
            pt: 0,
            pb: 2,
          }}
        >
          <NotificationBell
            isFolded={isFolded}
            showText={showText}
            onOpenChangelog={() => setWhatsNewOpen(true)}
          />
          {/* Même trait que celui du menu ; l'écart des entrées suffit à l'espacer pareil. */}
          <Divider sx={{ borderColor: 'var(--nav-border-color)', m: 0 }} />
          {changelogHasReleases && (
            <Tooltip
              disableHoverListener={!isFolded}
              enterDelay={300}
              placement="right"
              title={tWhatsNew('menuTitle')}
            >
              <Box sx={{ display: 'block', width: '100%' }}>
                <ButtonBase
                  onClick={() => setWhatsNewOpen(true)}
                  aria-label={isFolded ? tWhatsNew('menuTitle') : undefined}
                  sx={{
                    alignItems: 'center',
                    borderLeft: `${densityPx(3)}px solid transparent`,
                    borderRadius: 1,
                    display: 'flex',
                    gap: !isFolded ? 1 : 0,
                    justifyContent: 'flex-start',
                    pl: `${
                      SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX - densityPx(3)
                    }px`,
                    pr: densityPxStr(16),
                    height: densityPxStr(40),
                    textAlign: 'left',
                    width: '100%',
                    '&:hover': sideNavButtonHoverSx,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      alignItems: 'center',
                      color: 'var(--nav-item-icon-color)',
                      display: 'inline-flex',
                      flex: '0 0 auto',
                      height: densityPxStr(28),
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <SvgIcon fontSize="small">
                      <Stars01 fontSize="small" />
                    </SvgIcon>
                    {showNewBadge && isFolded && (
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          backgroundColor: 'primary.main',
                          border: '1.5px solid var(--nav-bg)',
                          borderRadius: '50%',
                          height: 8,
                          position: 'absolute',
                          right: 2,
                          top: 2,
                          width: 8,
                        }}
                      />
                    )}
                  </Box>
                  {!isFolded && (
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexGrow: 1,
                        gap: 1.5,
                        minWidth: 0,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        marginLeft: densityPxStr(3),
                        opacity: showText ? 1 : 0,
                        transform: showText
                          ? 'translateX(0)'
                          : `translateX(-${densityPx(10)}px)`,
                        transition: 'opacity 0.3s ease, transform 0.3s ease',
                        pointerEvents: showText ? 'auto' : 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--nav-item-color)',
                          flex: '0 1 auto',
                          fontFamily: (theme) => theme.typography.fontFamily,
                          fontSize: SIDE_NAV_ITEM_FONT_SIZE,
                          fontWeight: 300,
                          lineHeight: densityPxStr(28),
                          minWidth: 0,
                        }}
                      >
                        {tWhatsNew('menuTitle')}
                      </Typography>
                      {showNewBadge && (
                        <Box
                          component="span"
                          sx={{
                            alignItems: 'center',
                            backgroundColor: 'primary.main',
                            borderRadius: '10px',
                            color: '#fff',
                            display: 'inline-flex',
                            flex: '0 0 auto',
                            fontSize: '9px',
                            fontWeight: 700,
                            letterSpacing: '.3px',
                            padding: '2px 6px',
                          }}
                        >
                          {tWhatsNew('newBadge')}
                        </Box>
                      )}
                    </Box>
                  )}
                </ButtonBase>
              </Box>
            </Tooltip>
          )}
          <SideNavProductTourEntry isFolded={isFolded} showText={showText} />
          <SideNavHelpEntry isFolded={isFolded} showText={showText} />
          <SideNavAccountFooter
            accountMenuOpenRef={accountMenuOpenRef}
            isFolded={isFolded}
            user={auth.user}
            showText={showText}
          />
          <VersionDisplayer showText={showText} />
        </Box>
        <WhatsNewModal
          open={whatsNewOpen}
          releases={changelogReleases}
          isLoading={changelogLoading}
          isEmpty={changelogEmpty}
          onClose={() => setWhatsNewOpen(false)}
          onAcknowledge={acknowledgeChangelogRelease}
        />
      </Stack>
    </Drawer>
  )
}
